/* Built In Imports */
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

/* External Imports */
import { useMediaQuery } from 'react-responsive';

/* Internal Imports */
/* Components */
import { getCookie } from '@components/Utility/utils';
import config from '@config';
import { usePageType } from '@hooks/usePageType';
import { reportProgramConversion } from '@ieo/Components/Form/ieo/api/analyticsAPi';
import { authenticateUser } from '@ieo/Components/ThankYou/ThankYouPageApi';
import useTimeout from '@ieo/Components/ThankYou/useTimeoutFn';

/* Services */

/**
 *
 * @returns
 */
export const usePageState = () => {
  const router = useRouter();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [isGiftingFlow, setIsGiftingFlow] = useState();
  const [pageState, setPageState] = useState({
    status: 'loading',
    variant: null,
    authStatus: 'idle',
  });
  const { authStatus } = pageState;
  const { isGiftPage } = usePageType();

  useEffect(() => {
    if (router.asPath.indexOf('/gift') > -1) {
      setIsGiftingFlow(true);
    }
  }, [router.asPath, pageState]);

  const setMixPanelCookie = transactionId =>
    !getCookie(`mp-event-${transactionId}`) &&
    (document.cookie = `mp-event-${transactionId}=${JSON.stringify({
      txnId: transactionId,
    })};expires=${new Date(
      Date.now() + 1000 * 60 * 60 * 24
    ).toGMTString()};domain=${
      config.COOKIE_DOMAIN
    };path=/;secure=true;samesite=true;`);

  useTimeout(
    () => {
      const newAuthStatus = 'waited30s';
      setPageState(state => ({
        ...state,
        authStatus: newAuthStatus,
      }));
    },
    authStatus === 'loading' ? 30 * 1000 : null
  );

  const ranEffect = useRef(false);
  useEffect(() => {
    if (ranEffect.current || !router.isReady) return;

    ranEffect.current = true;

    const registrationData = JSON.parse(
      sessionStorage.getItem('registrationData')
    );

    const {
      token,
      status,
      registrationType,
      requestId,
      fullPrice,
      programLanguage,
      transactionId,
      flow,
    } = router.query;

    const convertGiftingFlowData = data => {
      data = { ...data, ...data.data };
      if (data?.amount) {
        data.pmtCurr = data.amount.split(' ')[0];
        data.tCost = parseInt(data.amount.split(' ')[1]);
      }
      return data;
    };

    // Get user transaction information via API
    if (token && (!status || status.toLowerCase() === 'completed')) {
      setPageState(state => ({ ...state, authStatus: 'loading' }));
      const type = registrationType;
      authenticateUser({
        token: token,
        requestId,
        vId: isMobile ? 'MOBIL' : 'AOEI',
        isGiftingFlow: router.asPath.indexOf('/gift') > -1,
      })
        .then(data => {
          if (!data) return;

          setPageState(state => ({
            ...state,
            status: 'done',
            authStatus: 'done',
            variant: 'token-data',
            data: data,
            type,
            message: data.message,
            // TODO: Include this data in test
          }));

          data.item_category3 = registrationType;
          if (isGiftingFlow) {
            data = convertGiftingFlowData(data);
          }
          if (!isPurchaseEventFired && setMixPanelCookie(data.txnId)) {
            isPurchaseEventFired = true;
            reportProgramConversion(
              data,
              {
                fullPrice,
                language: programLanguage,
                flow,
              },
              isGiftPage
            );
          }
        })
        .catch(error => {
          console.error(error);
          setPageState(state => ({
            ...state,
            status: 'error',
            msg: error?.message,
          }));
        });
      // Show status from GPMS
    } else if (status) {
      if (
        status?.toLowerCase() !== 'error' &&
        (isGiftingFlow || router.asPath.indexOf('/gift') > -1)
      ) {
        authenticateUser({
          token: token,
          requestId,
          isGiftingFlow: isGiftingFlow || router.asPath.indexOf('/gift') > -1,
          vId: isMobile ? 'MOBIL' : 'AOEI',
        })
          .then(data => {
            if (!data) return;
            if (data.status) {
              setPageState(state => ({
                ...state,
                transactionId: transactionId,
                status: 'done',
                authStatus: 'done',
                variant: 'token-data',
                data: data,
                messageStatus: data.status,
              }));
              data.item_category3 = registrationType;
              if (isGiftingFlow) {
                data = convertGiftingFlowData(data);
              }
              if (!isPurchaseEventFired && setMixPanelCookie(transactionId)) {
                isPurchaseEventFired = true;
                reportProgramConversion(
                  data,
                  {
                    fullPrice,
                    language: programLanguage,
                  },
                  isGiftPage
                );
              }
            } else {
              setPageState(state => ({
                ...state,
                transactionId: transactionId,
                status: 'show-message',
                data: data,
                messageStatus: data?.error?.code
                  ? 'error'
                  : status.toLowerCase(),
              }));
            }
          })
          .catch(error => {
            // console.log('errrrrrrr', router.query, error);
            console.error(error);
            setPageState(state => ({
              ...state,
              status: 'error',
              msg: error?.message,
            }));
          });
      } else {
        setPageState(state => ({
          ...state,
          transactionId: transactionId,
          status: 'show-message',
          messageStatus: status.toLowerCase(),
        }));
      }
      // console.log('gtm', country[0]);
      // We have registration data from local storage,
      // this must have been a free transfer (without GPMS)
    } else if (registrationData) {
      setPageState(state => ({
        ...state,
        status: 'done',
        variant: 'location-data',
        data: {
          ...registrationData,
          fname: registrationData.firstName,
          lname: registrationData.lastName,
        },
        programType: registrationData.pgmTypeId,
      }));

      reportProgramConversion(
        {
          txnId: transactionId,
          tCost: 0,
          pmtCurr: registrationData.currency,
          country: registrationData.country,
          item_category3: 'Free_User',
        },
        {
          fullPrice,
          language: programLanguage,
        },
        isGiftPage
      );
      // If we get here, the URL is not valid
    } else {
      // console.log('ininini222', router.query);
      setPageState(state => ({ ...state, status: 'invalid' }));
    }
    // console.log('ininini444', router.query);
  });

  return pageState;
};
